import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Popover } from '@headlessui/react';
import { usePopper } from 'react-popper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { LOCATION_TYPES } from '../../../../constants/general.constants';
import { FilterContext } from '../FilterSection';
import { TextInput } from '../../TextInput';
import { Typography } from '../../Typography';
import { Checkbox } from '../../Checkbox';
import * as S from '../FilterSection.styles';

const ITEMS_ARRAY = LOCATION_TYPES;
const ACTIONS = {
	SET: 'SET_LOCATION_TYPES',
	ADD_FILTER: 'ADD_TO_ACTIVE_FILTERS',
	REMOVE_FILTER: 'REMOVE_FROM_ACTIVE_FILTERS',
};
const FILTER_NAME = 'locationTypes';
const LABEL = 'Location Type';

export const LocationTypeFilter = ({ dispatch }) => {
	const { state } = useContext(FilterContext);
	const { activeFilters, locationTypes: filterArray } = state;

	const [referenceElement, setReferenceElement] = useState(null);
	const [popperElement, setPopperElement] = useState(null);
	const { styles, attributes } = usePopper(referenceElement, popperElement, {
		placement: 'bottom-start',
		modifiers: [
			{
				name: 'offset',
				options: {
					offset: [0, 12],
				},
			},
		],
	});

	const [searchFilter, setSearchFilter] = useState('');

	const filteredItems = useMemo(() => {
		return ITEMS_ARRAY.filter((item) => {
			return item.name.toLowerCase().includes(searchFilter.toLowerCase());
		});
	}, [searchFilter]);

	const clearValue = (e) => {
		e?.stopPropagation();
		dispatch({ type: ACTIONS.SET, payload: [] });
		dispatch({ type: ACTIONS.REMOVE_FILTER, payload: FILTER_NAME });
	};

	const onChangeHandler = (e, value) => {
		if (e.target.checked) {
			dispatch({ type: ACTIONS.SET, payload: [...filterArray, value] });
			if (!activeFilters.includes(FILTER_NAME)) {
				dispatch({ type: ACTIONS.ADD_FILTER, payload: FILTER_NAME });
			}
		} else {
			const newArray = filterArray.filter((type) => type !== value);
			if (newArray.length === 0) {
				clearValue();
			} else {
				dispatch({ type: ACTIONS.SET, payload: newArray });
			}
		}
	};

	const getButtonClassName = useMemo(() => {
		let className = 'popover-button';
		if (filterArray?.length > 0) {
			className += ' active';
		}
		return className;
	}, [filterArray]);

	useEffect(() => {
		if (filterArray.length > 0) {
			dispatch({ type: 'ADD_TO_ACTIVE_FILTERS', payload: FILTER_NAME });
		}
	}, []);

	return (
		<Popover as={S.SingleFilterWrapper}>
			<Popover.Button ref={setReferenceElement} className={getButtonClassName}>
				{filterArray?.length > 0 ? (
					<>
						<Typography tag="p" variation="2">
							{filterArray?.map((type) => type?.name).join(', ')}
						</Typography>
						<FontAwesomeIcon icon={['fal', 'times']} onClick={clearValue} />
					</>
				) : (
					<Typography tag="p" variation="2">
						{LABEL}
					</Typography>
				)}
			</Popover.Button>
			<Popover.Panel ref={setPopperElement} style={styles.popper} {...attributes.popper} className="popover-panel">
				<div className="popover-panel-wrapper">
					<div className="checkbox-list-wrapper">
						<TextInput value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)} type="search" icon={['fal', 'search']} size="small" placeholder="Search" />
						{filteredItems.map((item) => (
							<div key={item.id} className="checkbox-field">
								<Checkbox id={item.id} value={item.id} checked={filterArray.includes(item)} onChange={(e) => onChangeHandler(e, item)} />
								<label htmlFor={item.id}>{item.name}</label>
							</div>
						))}
					</div>
				</div>
			</Popover.Panel>
		</Popover>
	);
};

LocationTypeFilter.displayName = 'LocationTypeFilter';

LocationTypeFilter.propTypes = {
	dispatch: PropTypes.func.isRequired,
};
